import React from "react";
import { styled } from "@mui/material/styles";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import buttonGroupClasses from "@mui/material/ButtonGroup/buttonGroupClasses";
import buttonClasses from "@mui/material/Button/buttonClasses";
import Tooltip from "@mui/material/Tooltip";
import { getTooltipSx } from "@hyro-components/Tooltip.styles";
import { TOOLTIP_DELAY } from "@utils";
import { HYRO_BUTTON_GROUP_TEST_ID, hyroButtonGroupOptionTestId } from "./HyroButtonGroup.consts";

const HyroButtonGroup = ({
  options,
  selectedOption = null,
  onChange,
  disableEmpty = false,
  disabled = false,
  disabledOptions = [],
  optionTooltips = {}
}) => {
  const handleClick = (option) => {
    if (disabledOptions.includes(option)) return;
    const next = option === selectedOption && !disableEmpty ? null : option;
    if (next !== selectedOption) onChange(next);
  };

  return (
    <StyledButtonGroup variant="contained" disabled={disabled} data-testid={HYRO_BUTTON_GROUP_TEST_ID}>
      {/* A disabled option stays focusable (aria-disabled) so its tooltip can still explain why. */}
      {options.map((option) => (
        <Tooltip
          key={option}
          title={optionTooltips[option] ?? ""}
          placement="bottom"
          enterDelay={TOOLTIP_DELAY.SHORT}
          slotProps={{ tooltip: { sx: getTooltipSx() } }}
        >
          <StyledButton
            selected={selectedOption === option}
            unavailable={disabledOptions.includes(option)}
            aria-disabled={disabledOptions.includes(option) || undefined}
            onClick={() => handleClick(option)}
            disableRipple
            data-testid={hyroButtonGroupOptionTestId(option, selectedOption === option)}
          >
            {option}
          </StyledButton>
        </Tooltip>
      ))}
    </StyledButtonGroup>
  );
};

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.system[0],
  border: `1px solid ${theme.palette.grey[300]}`,
  [`&.${buttonGroupClasses.root}`]: {
    boxShadow: "none",
  },
  [`& .${buttonGroupClasses.grouped}:not(:last-of-type)`]: {
    borderRight: `1px solid ${theme.palette.grey[300]}`
  },
}));

const StyledButton = styled(Button, { shouldForwardProp: (prop) => !["selected", "unavailable"].includes(prop) })(({ theme, selected, unavailable }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: selected ? theme.palette.tertiary[300] : theme.palette.system[0],
  color: unavailable ? theme.palette.text[600] : theme.palette.text[700],
  ...theme.typography.buttonMedium,
  ...(unavailable && { cursor: "not-allowed" }),
  "&:hover": {
    backgroundColor: selected ? theme.palette.tertiary[200] : theme.palette.system[50],
    ...(unavailable && { backgroundColor: theme.palette.system[0] })
  },
  [`&.${buttonClasses.contained}`]: {
    boxShadow: "none",
  },
  [`&.${buttonClasses.disabled}`]: {
    backgroundColor: selected ? theme.palette.tertiary[300] : theme.palette.system[0],
    color: theme.palette.text[600]
  }
}));

export default HyroButtonGroup;
